import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Model from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { not, or } from 'ember-truth-helpers';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import PaginatedSelectMultiple from 'garaje/components/paginated-select/multiple';
import type Flow from 'garaje/models/flow';
import type GlobalFlow from 'garaje/models/global-flow';
import type Questionnaire from 'garaje/models/questionnaire';

const DEFAULT_RETAKE_AFTER_DAYS = 30;

interface QuestionnaireSettingsFormSignature {
  Args: {
    fetchFlowsPage: (page: number, searchTerm: string) => Array<Flow | GlobalFlow> | Promise<Array<Flow | GlobalFlow>>;
    flows: Array<Flow | GlobalFlow>;
    onChange: (models: Model[]) => void;
    questionnaire: Questionnaire;
    readOnly?: boolean;
    updateSelectedFlows: (flows: Array<Flow | GlobalFlow>) => void;
  };
  Element: HTMLFormElement;
}

export default class QuestionnaireSettingsForm extends Component<QuestionnaireSettingsFormSignature> {
  flowSelectId = guidFor(this);

  // Store the initial value of @questionnaire.validityDays; if the user changes the "retake interval" setting to
  // something other than "retake after X days" we want to preserve the original value in the field.
  @tracked lastValidityDays = this.args.questionnaire.validityDays || null; // use ||, not ??, because we want this to default to `null` even if @questionnaire.validityDays === 0

  get neverRetake(): boolean {
    return this.args.questionnaire.validityDays === 0;
  }

  get retakeAfterDays(): boolean {
    const validityDays = this.args.questionnaire.validityDays;
    return validityDays !== null && validityDays > 0;
  }

  get retakeEveryVisit(): boolean {
    return this.args.questionnaire.validityDays === null;
  }

  get selectedFlows(): Array<Flow | GlobalFlow> {
    return this.args.questionnaire.flowQuestionnaires.filter((fq) => !fq.isDeleted).map((fq) => fq.flow);
  }

  get validityDays(): number {
    const savedValidityDays = this.args.questionnaire.validityDays;
    const lastValidityDays = this.lastValidityDays;

    if (savedValidityDays !== null && savedValidityDays > 0) return savedValidityDays;
    return lastValidityDays ?? DEFAULT_RETAKE_AFTER_DAYS;
  }

  @action
  updateValidityDays(days: '' | number | null): void {
    if (days === '') days = DEFAULT_RETAKE_AFTER_DAYS;
    const questionnaire = this.args.questionnaire;
    questionnaire.validityDays = days;
    if (days !== null && days > 0) {
      this.lastValidityDays = days;
    }
    this.args.onChange([questionnaire]);
  }

  <template>
    <form class="flex gap-6" ...attributes>
      <div class="flex-1" data-test-questionnaire-flows>
        <h2 class="text-base my-4">Sign-in flows</h2>
        <label for={{this.flowSelectId}}>
          Apply to sign-in flows
        </label>

        <FlowSelector
          @fetchFlowsPage={{@fetchFlowsPage}}
          @flows={{@flows}}
          @includeLocationPrefix={{@questionnaire.isGlobal}}
          @readOnly={{@readOnly}}
          @selectedFlows={{this.selectedFlows}}
          @triggerId={{this.flowSelectId}}
          @updateSelectedFlows={{@updateSelectedFlows}}
        />
      </div>

      <div class="flex-1">
        <h2 class="text-base mt-4 mb-2">Response validity period</h2>
        <p class="text-sm mb-0">Choose when returning visitors must re-take this assessment.</p>
        <div class="flex items-center">
          <label class="!text-sm !text-carbon-70">
            <input
              type="radio"
              name="retake-interval"
              value="days"
              checked={{this.retakeAfterDays}}
              disabled={{@readOnly}}
              {{on "change" (fn this.updateValidityDays (or this.lastValidityDays DEFAULT_RETAKE_AFTER_DAYS))}}
            />
            Re-take assessment after a number of days:
          </label>
          <input
            type="number"
            name="retake-days"
            disabled={{or (not this.retakeAfterDays) @readOnly}}
            class="!w-16 ml-4 !mb-0"
            min="1"
            data-test-retake-days
            value={{this.validityDays}}
            {{on "input" (pick "target.value" this.updateValidityDays)}}
          />
        </div>
        <label class="block mb-2 !text-sm !text-carbon-70">
          <input
            type="radio"
            name="retake-interval"
            value="every-visit"
            checked={{this.retakeEveryVisit}}
            disabled={{@readOnly}}
            {{on "change" (fn this.updateValidityDays null)}}
          />
          Re-take assessment for every visit
        </label>
        <label class="block !text-sm !text-carbon-70">
          <input
            type="radio"
            name="retake-interval"
            value="never"
            checked={{this.neverRetake}}
            disabled={{@readOnly}}
            {{on "change" (fn this.updateValidityDays 0)}}
          />
          Never retake assessment
        </label>
      </div>
    </form>
  </template>
}

interface FlowSelectorSignature {
  Args: {
    fetchFlowsPage: QuestionnaireSettingsFormSignature['Args']['fetchFlowsPage'];
    flows: Array<Flow | GlobalFlow>;
    includeLocationPrefix: boolean;
    readOnly?: boolean;
    selectedFlows: Array<Flow | GlobalFlow>;
    triggerId: string;
    updateSelectedFlows: (selectedFlows: Array<Flow | GlobalFlow>) => void;
  };
}

class FlowSelector extends Component<FlowSelectorSignature> {
  // Make sure TS knows to use the right type for the generic component
  PaginatedSelectMultiple = PaginatedSelectMultiple<Flow | GlobalFlow>;

  get sortedSelectedFlows(): Array<Flow | GlobalFlow> {
    return this.args.selectedFlows.toSorted((a, b) => {
      // sort global flows before location flows
      if (a.isGlobal && !b.isGlobal) return -1;
      if (!a.isGlobal && b.isGlobal) return 1;

      // Now, both of of these flows will be of the same type (location or global).
      // If they're location flows, sort by location name first.
      if (!a.isGlobal && !b.isGlobal) {
        const aLocation = a.location.content!;
        const bLocation = b.location.content!;

        const locationNameComparison = aLocation.name.localeCompare(bLocation.name);
        if (locationNameComparison !== 0) return locationNameComparison;
      }

      // If we're here, either both of these are global flows, or both are location flows for the same location.
      // Sort them by flow name.
      return a.name.localeCompare(b.name);
    });
  }

  @action // need @action for the correct this-binding
  flowDisplayName(flow: Flow | GlobalFlow): string {
    if (flow.isGlobal) return flow.name;

    if (!this.args.includeLocationPrefix) return flow.name;

    if (flow.location.content) {
      const locationName = flow.location.content.name;
      return `${locationName} • ${flow.name}`;
    }

    return flow.name;
  }

  <template>
    <this.PaginatedSelectMultiple
      @disabled={{@readOnly}}
      @dropdownClass="in-modal"
      @fetchOptions={{@fetchFlowsPage}}
      @onChange={{@updateSelectedFlows}}
      @options={{@flows}}
      @selected={{this.sortedSelectedFlows}}
      @triggerId={{@triggerId}}
      as |flow|
    >
      <GlobalFlowIcon @show={{flow.isGlobal}}>
        {{this.flowDisplayName flow}}
      </GlobalFlowIcon>
    </this.PaginatedSelectMultiple>
  </template>
}
